import { FormikProps } from "formik"
import { get } from "lodash"
import { Input } from "components/input"
import PhoneNumberInput from "components/input/phoneNumberInput"
import { IconButton } from "components/button"
import { ContactInfo } from "types/customer"
import { RiDeleteBin7Line } from "react-icons/ri"
import FavoriteStarButton from "./FavoriteStarButton"

export const emptyExtraEmail = (): ContactInfo => ({
  contact_type: "email",
  contact_value: "",
  contact_description: "",
  is_billing: false,
  is_notification: false,
})

export const emptyExtraPhone = (): ContactInfo => ({
  contact_type: "phone",
  contact_value: "",
  contact_description: "",
  is_billing: false,
  is_notification: false,
})

type Props = {
  formik: FormikProps<any>
  basePath: string
  labels: {
    section?: string
    addEmail: string
    addPhone: string
    billing: string
    notification: string
    description?: string
    emailPlaceholder?: string
    phonePlaceholder?: string
    makePrimary?: string
  }
  /** When set (primary contact section), show star on the extra row selected as default. */
  defaultPrimaryEmailKey?: string
  defaultPrimaryPhoneKey?: string
  /** When provided (primary contact section), an extra row's star button can promote that row to primary. */
  onSetDefaultPrimaryEmailKey?: (key: string) => void
  onSetDefaultPrimaryPhoneKey?: (key: string) => void
}

const ContactFields = ({
  formik,
  basePath,
  labels,
  defaultPrimaryEmailKey,
  defaultPrimaryPhoneKey,
  onSetDefaultPrimaryEmailKey,
  onSetDefaultPrimaryPhoneKey,
}: Props) => {
  const list = (get(formik.values, basePath) as ContactInfo[] | undefined) || []

  const setList = (next: ContactInfo[]) => {
    formik.setFieldValue(basePath, next, true)
  }

  const addEmail = () => {
    setList([...list, emptyExtraEmail()])
  }

  const addPhone = () => {
    setList([...list, emptyExtraPhone()])
  }

  const removeAt = (idx: number) => {
    const next = [...list]
    next.splice(idx, 1)
    setList(next)
  }

  const toggleField = (idx: number, field: "is_billing" | "is_notification") => {
    const next = [...list]
    if (!next[idx]) return
    next[idx] = { ...next[idx], [field]: !next[idx][field] }
    setList(next)
  }

  return (
    <div className="flex flex-col gap-3 mt-2">
      {labels.section ? (
        <h4 className="text-sm font-semibold text-slate-700">{labels.section}</h4>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="text-sm text-blue-600 hover:underline"
          onClick={addEmail}
        >
          + {labels.addEmail}
        </button>
        <span className="text-slate-300">|</span>
        <button
          type="button"
          className="text-sm text-blue-600 hover:underline"
          onClick={addPhone}
        >
          + {labels.addPhone}
        </button>
      </div>
      {list.map((item, idx) => {
        const namePrefix = `${basePath}.${idx}`
        if (item.contact_type === "email") {
          const thisKey = `extra:${idx}`
          const isPrimary = defaultPrimaryEmailKey === thisKey
          return (
            <div
              key={`${basePath}-email-${idx}`}
              className="flex flex-col gap-2 p-3 border border-slate-200 rounded-md"
            >
              <div className="flex justify-between items-start gap-2">
                <div
                  className="relative group"
                  style={{ width: "49%" }}
                >
                  {onSetDefaultPrimaryEmailKey ? (
                    <FavoriteStarButton
                      isPrimary={isPrimary}
                      onClick={() => onSetDefaultPrimaryEmailKey(thisKey)}
                    />
                  ) : null}
                  <Input
                    label={labels.emailPlaceholder || "Email"}
                    formik={formik}
                    name={`${namePrefix}.contact_value`}
                    style={{ width: "100%" }}
                  />
                </div>
                <Input
                  label={labels.description || "Description"}
                  formik={formik}
                  name={`${namePrefix}.contact_description`}
                  maxLength={255}
                  style={{ width: "49%" }}
                />
                <IconButton
                  type="button"
                  title="Remove"
                  className="mt-6"
                  onClick={() => removeAt(idx)}
                >
                  <RiDeleteBin7Line className="text-red-500" />
                </IconButton>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!item.is_billing}
                    onChange={() => toggleField(idx, "is_billing")}
                  />
                  <span>{labels.billing}</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!item.is_notification}
                    onChange={() => toggleField(idx, "is_notification")}
                  />
                  <span>{labels.notification}</span>
                </label>
              </div>
            </div>
          )
        }
        const thisPhoneKey = `extra:${idx}`
        const isPhonePrimary = defaultPrimaryPhoneKey === thisPhoneKey
        return (
          <div
            key={`${basePath}-phone-${idx}`}
            className="flex flex-col gap-2 p-3 border border-slate-200 rounded-md"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="relative group" style={{ width: "49%" }}>
                {onSetDefaultPrimaryPhoneKey ? (
                  <FavoriteStarButton
                    isPrimary={isPhonePrimary}
                    onClick={() => onSetDefaultPrimaryPhoneKey(thisPhoneKey)}
                  />
                ) : null}
                <PhoneNumberInput
                  label={labels.phonePlaceholder || "Phone"}
                  formik={formik}
                  name={`${namePrefix}.contact_value`}
                  style={{ width: "100%" }}
                />
              </div>
              <Input
                label={labels.description || "Description"}
                formik={formik}
                name={`${namePrefix}.contact_description`}
                maxLength={255}
                style={{ width: "49%" }}
              />
              <IconButton
                type="button"
                title="Remove"
                className="mt-6"
                onClick={() => removeAt(idx)}
              >
                <RiDeleteBin7Line className="text-red-500" />
              </IconButton>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={!!item.is_notification}
                onChange={() => toggleField(idx, "is_notification")}
              />
              <span>{labels.notification}</span>
            </label>
          </div>
        )
      })}
    </div>
  )
}

export default ContactFields
